import { useState } from 'react'
import { postDraft } from '../api/draft'
import type { DraftResponse, UnreadAttachment } from '../types'
import { Linked } from './Linked'

interface Props {
  value: string
  onChange: (v: string) => void
  unread: UnreadAttachment[]
  /** Which model the backend actually used, not which one was requested. */
  model?: DraftResponse['model']
  ticketId?: string
  /** Inside the Desk widget, where the reply editor can be written to. */
  embedded?: boolean
}

export function DraftEditor({ value, onChange, unread, model, ticketId, embedded }: Props) {
  const [busy, setBusy] = useState('')
  const [done, setDone] = useState('')
  const [error, setError] = useState('')
  // Sending has no undo, so it takes two presses. Adding a note does not: a
  // private comment is deletable, which is the whole reason it is the safe one.
  const [armed, setArmed] = useState(false)

  /**
   * Put the draft in Desk's own reply editor and let the agent send it there.
   *
   * Sending through the API needs an address Zoho has verified, and a help
   * desk that never set one up cannot send at all. Desk's editor has no such
   * problem: it picks the address it always would, carries the signature, and
   * leaves the agent looking at what they are about to send.
   */
  function insertIntoDesk() {
    setError('')
    setDone('')
    setBusy('insert')

    const onDone = (e: MessageEvent) => {
      if (e.data?.type !== 'INSERT_REPLY_DONE') return
      window.removeEventListener('message', onDone)
      setBusy('')
      if (e.data.ok) setDone('Put in the Desk reply box below. Press Send there.')
      else setError(e.data.error || 'Desk would not take the reply.')
    }

    window.addEventListener('message', onDone)
    window.parent.postMessage({ type: 'INSERT_REPLY', text: value }, '*')

    // The widget host answers immediately in practice; this is only so the
    // button cannot sit on "Opening…" forever if the message is lost.
    setTimeout(() => {
      window.removeEventListener('message', onDone)
      setBusy((b) => {
        if (b !== 'insert') return b
        // Not an error: Desk often takes the draft without ever answering.
        setDone('Check the Desk reply box below. If the draft is not there, the extension in Desk is out of date.')
        return ''
      })
    }, 8000)
  }

  /**
   * Copy by whichever route this browser allows.
   *
   * navigator.clipboard needs the clipboard-write permission, which a
   * cross-origin iframe only has if the host grants it — and Desk does not, so
   * inside the widget this silently did nothing at all. execCommand copies the
   * page's own selection and works from inside the frame.
   */
  async function copy() {
    setError('')
    try {
      await navigator.clipboard.writeText(value)
      setDone('Copied')
      return
    } catch {
      // Fall through.
    }

    try {
      const box = document.createElement('textarea')
      box.value = value
      box.style.position = 'fixed'
      box.style.opacity = '0'
      document.body.appendChild(box)
      box.select()
      const ok = document.execCommand('copy')
      box.remove()
      if (ok) {
        setDone('Copied')
        return
      }
    } catch {
      // Nothing left to try.
    }

    setError('Could not copy. Select the text above and copy it by hand.')
  }

  async function post(as: 'note' | 'reply') {
    if (!ticketId) return
    if (as === 'reply' && !armed) return setArmed(true)

    setBusy(as)
    setError('')
    setDone('')
    try {
      const res = await postDraft(ticketId, value, as)
      setDone(as === 'note' ? 'Added as a private note.' : `Sent to ${res.to ?? 'the customer'}.`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy('')
      // In finally, not after the await: a send that failed used to leave this
      // armed, so the next click went straight out with no second press.
      setArmed(false)
    }
  }

  return (
    <div className="card">
      <div className="draft-head">
        <h2>Suggested reply</h2>

        {model && (
          <span className="label model-name" title={`${model.label} · ${model.name}`}>
            {model.label} · {model.name}
          </span>
        )}

        <button className="icon-text" onClick={() => void copy()}>
          {done === 'Copied' ? 'Copied' : 'Copy'}
        </button>
      </div>

      <textarea
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setDone('')
          setArmed(false)
        }}
        spellCheck
        aria-label="Suggested reply"
      />

      <div className={embedded ? 'draft-actions three' : 'draft-actions'}>
        <button
          className="secondary"
          disabled={Boolean(busy) || !ticketId || !value.trim()}
          onClick={() => void post('note')}
        >
          {busy === 'note' ? 'Adding…' : 'Add as note'}
        </button>

        {embedded && (
          <button
            className="secondary"
            disabled={Boolean(busy) || !value.trim()}
            onClick={insertIntoDesk}
          >
            {busy === 'insert' ? 'Opening…' : 'Edit in Desk'}
          </button>
        )}

        <button
          disabled={Boolean(busy) || !ticketId || !value.trim()}
          onClick={() => void post('reply')}
        >
          {busy === 'reply' ? 'Sending…' : armed ? 'Press again to send' : 'Send reply'}
        </button>
      </div>

      {armed && !busy && (
        <p className="note">This goes to the customer. There is no undo.</p>
      )}

      {done && done !== 'Copied' && <p className="note done">{done}</p>}
      {error && <p className="inline-error"><Linked text={error} /></p>}

      {unread.length > 0 && (
        <p className="note">Not analysed in this draft: {unread.map((a) => a.name).join(', ')}</p>
      )}
    </div>
  )
}
